import { Card, Col, Grid, Row, Radio, Divider, Modal } from "antd";
import type { RadioChangeEvent } from "antd";
import styles from "./SelectAgeSection.module.css";
import { MyButton, MyText } from "../../..";
import sectionImage1 from "../../../../assets/images/pages/DiagnosticsMarathon/section-2-1-img.svg"
import sectionImage2 from "../../../../assets/images/pages/DiagnosticsMarathon/section-2-2-img.svg"
import sectionImage3 from "../../../../assets/images/pages/DiagnosticsMarathon/section-2-3-img.svg"
import { useState } from 'react';
import { useEmbeddedNavigate } from "../../../../utils/context/embed";
import { ChildSelectModal } from "../../MarathonSpeech/ChildSelectModal";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedChildId, setDiagnosticResult } from "../../../../utils";

interface Step {
  readonly number: string;
  readonly text: string;
  readonly age: string;
  readonly img: string;
  readonly ageKey: string;
}

const STEPS: Step[] = [
  { number: "01", text: "(12-18 месяцев)", age: '1-1,5 года', img: sectionImage1, ageKey: '1-1.5' },
  { number: "02", text: "(19-36 месяцев)", age: '2-3 года', img: sectionImage2, ageKey: '2-3' },
  { number: "03", text: "(37-48 месяцев)", age: '3-4 года', img: sectionImage3, ageKey: '3-4' },
];

const QUESTIONS: Record<string, string[]> = {
  '1-1.5': [
    'Использует 10–50 разных слов, включая существительные и глаголы',
    'Составляет первые простые словосочетания (например, «мама дай», «дай мяч»)',
    'Понимает простые инструкции без жестов (например, «иди сюда»)',
    'Подражает звукам животных, предметов (например, «гав», «мяу»)',
    'Использует жесты для выражения просьб и эмоций',
  ],
  '2-3': [
    'Активный словарный запас 200–300 слов',
    'Составляет короткие фразы из 2–3 слов (например, «мама дай мяч»)',
    'Понимает и выполняет инструкции из 2–3 частей (например, «возьми мяч и дай маме»)',
    'Повторяет новые слова и предложения после взрослого',
    'Проявляет инициативу в общении, задаёт простые вопросы',
  ],
  '3-4': [
    'Словарный запас 1000+ слов',
    'Составляет фразы из 4–5 слов',
    'Понимает и рассказывает простые истории или события',
    'Правильно использует местоимения и простые предлоги',
    'Может участвовать в диалогах и пересказывать знакомые истории',
  ],
};

const AGE_LABELS: Record<string, string> = {
  '1-1.5': 'Возраст Вашего ребёнка 1-1,5 года (12-18 месяцев)',
  '2-3':   'Возраст Вашего ребёнка 2-3 года (19-36 месяцев)',
  '3-4':   'Возраст Вашего ребёнка 3-4 года (37-48 месяцев)',
};

const RESULTS: Record<string, { min: number; max: number; title: string; description: string; color: string }[]> = {
  '1-1.5': [
    { min: 4, max: 5, title: 'Речевое развитие соответствует возрасту', description: 'Отличный результат! Ребёнок развивается в соответствии с возрастными нормами.', color: '#13C296' },
    { min: 2, max: 3, title: 'Возможна задержка', description: 'Рекомендуются дополнительные занятия с логопедом для поддержки речевого развития.', color: '#F5A623' },
    { min: 0, max: 1, title: 'Выраженная задержка', description: 'Необходима срочная консультация специалиста. Пожалуйста, обратитесь к логопеду или неврологу.', color: '#EF4444' },
  ],
  '2-3': [
    { min: 4, max: 5, title: 'Речевое развитие соответствует возрасту', description: 'Отличный результат! Ребёнок развивается в соответствии с возрастными нормами.', color: '#13C296' },
    { min: 2, max: 3, title: 'Задержка речи', description: 'Необходима консультация логопеда для оценки речевого развития.', color: '#F5A623' },
    { min: 0, max: 1, title: 'Значительная задержка', description: 'Обязательна консультация и обследование у специалиста.', color: '#EF4444' },
  ],
  '3-4': [
    { min: 4, max: 5, title: 'Соответствует норме', description: 'Речевое развитие ребёнка соответствует возрастным показателям.', color: '#13C296' },
    { min: 2, max: 3, title: 'Задержка', description: 'Рекомендуется логопедическая помощь для коррекции речевого развития.', color: '#F5A623' },
    { min: 0, max: 1, title: 'Серьёзная задержка', description: 'Необходима диагностика и коррекция. Обратитесь к специалисту.', color: '#EF4444' },
  ],
};

const getAgeKeyByMonths = (months: number): string | null => {
  if (months >= 12 && months <= 18) return '1-1.5';
  if (months >= 19 && months <= 36) return '2-3';
  if (months >= 37 && months <= 48) return '3-4';
  return null;
};

interface YesNoButtonProps {
  value: 'yes' | 'no' | null;
  onChange: (val: 'yes' | 'no' | null) => void;
}

interface SelectAgeSectionProps {
  childId?: number | string | null;
}

const YesNoButton = ({ value, onChange }: YesNoButtonProps) => {
  const handleChange = (e: RadioChangeEvent) => {
    onChange(value === e.target.value ? null : e.target.value);
  };

  return (
    <Radio.Group
      value={value}
      onChange={handleChange}
      optionType="button"
      style={{ display: 'flex' }}
    >
      <Radio.Button value="yes">Да</Radio.Button>
      <Radio.Button value="no">Нет</Radio.Button>
    </Radio.Group>
  );
};

const getResult = (yesCount: number, ageKey: string) => {
  const ageResults = RESULTS[ageKey] ?? RESULTS['1-1.5'];
  return ageResults.find(r => yesCount >= r.min && yesCount <= r.max) ?? ageResults[ageResults.length - 1];
};

export const SelectAgeSection: React.FC<SelectAgeSectionProps> = ({ childId }) => {
  const [answer, setAnswer] = useState<'yes' | 'no' | null>(null);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const dispatch = useDispatch();
  const isTablet = screens.md && !screens.xl && !screens.xxl;
  const [selectedAgeKey, setSelectedAgeKey] = useState<string>('1-1.5');
  const [answers, setAnswers] = useState<Record<string, 'yes' | 'no' | null>>({});
  const [modalVisible, setModalVisible] = useState(false);
  const embeddedNavigate = useEmbeddedNavigate();
  const [showFooter, setShowFooter] = useState(false);
  const [hovered, setHovered] = useState(false);
  const questions = QUESTIONS[selectedAgeKey] ?? [];
  const [childSelectVisible, setChildSelectVisible] = useState(true);
  const [localSelectedChildId, setLocalSelectedChildId] = useState<number | null>(null);
  const [childAgeLabel, setChildAgeLabel] = useState<string>('');


  const childrenList = useSelector(
    (state: any) => state.materials?.childrens?.data?.children ?? []
  );
  
  const handleChildConfirm = () => {
    const child = childrenList.find((c: any) => c.id === localSelectedChildId);
    if (child) {
      const ageKey = getAgeKeyByMonths(child.age_in_months);
      if (ageKey) {
        setSelectedAgeKey(ageKey);
        setAnswers({});
      }
      setChildAgeLabel(formatChildAge(child.age_in_months));
      dispatch(setSelectedChildId(child.id));
    }
    setChildSelectVisible(false);
  };

  const getYearLabel = (age: number) => {
    const lastDigit = age % 10;
    const lastTwoDigits = age % 100;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return 'лет';
    if (lastDigit === 1) return 'год';
    if (lastDigit >= 2 && lastDigit <= 4) return 'года';
    return 'лет';
  };

  const getMonthLabel = (age: number) => {
    const lastDigit = age % 10;
    const lastTwoDigits = age % 100;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return 'месяцев';
    if (lastDigit === 1) return 'месяц';
    if (lastDigit >= 2 && lastDigit <= 4) return 'месяца';
    return 'месяцев';
  };

  const formatChildAge = (ageInMonths: number) => {
    const years = Math.floor(ageInMonths / 12);
    const months = ageInMonths % 12;
    let result = '';
    if (years > 0) result += `${years} ${getYearLabel(years)}`;
    if (months > 0) result += (result ? ' ' : '') + `${months} ${getMonthLabel(months)}`;
    return result || '0 месяцев';
  };

  const handleAgeSelect = (ageKey: string) => {
    setSelectedAgeKey(ageKey);
    setAnswers({});
  };

  const handleShowResult = () => {
    const diagnosticResult = {
      date: new Date().toISOString(),
      ageKey: selectedAgeKey,
      questions: questions.map((question, index) => ({
        question,
        answer: getAnswer(index),
      })),
      yesCount,
      totalCount: questions.length,
      resultTitle: result.title,
    };

    if (localSelectedChildId) {
      dispatch(setDiagnosticResult({ childId: localSelectedChildId, result: diagnosticResult }));
    }

    setModalVisible(true);
    setShowFooter(true);
  };

  const handleAnswer = (index: number, val: 'yes' | 'no' | null) => {
    setAnswers(prev => ({ ...prev, [`${selectedAgeKey}-${index}`]: val }));
  };

  const getAnswer = (index: number) => answers[`${selectedAgeKey}-${index}`] ?? null;

  const answeredCount = questions.filter((_, i) => getAnswer(i) !== null).length;
  const allAnswered = answeredCount === questions.length;
  const yesCount = questions.filter((_, i) => getAnswer(i) === 'yes').length;
  const result = getResult(yesCount, selectedAgeKey);

 return (
    <section id="select-age" className={styles.section} aria-label="Диагностика речи">
      <div className={styles.container}>
        <h2 className={styles.title}>Возраст ребенка</h2>

        <Row gutter={[40, 40]}>
          <Col span={24}>
            <Row justify="center" align="middle" gutter={[32, 32]}>
              {STEPS.map((step) => (
                <Col key={step.number} xs={24} md={24} xl={8}>
                  <Card
                    // onClick={() => handleAgeSelect(step.ageKey)}
                    style={{
                      borderRadius: '14px',
                      border: `2px solid ${selectedAgeKey === step.ageKey ? '#F5A623' : '#E5E9F2'}`,
                      cursor: 'pointer',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <Row justify="space-between" align="middle" gutter={24}>
                      <Col span={4}>
                        <Row style={{ width: 80, height: 80 }}>
                          <img src={step.img} alt="age-img" />
                        </Row>
                      </Col>
                      <Col span={18}>
                        <Row gutter={[4, 4]}>
                          <Col span={24}>
                            <MyText color="#0A033C" size={20} weight={700} style={{ fontFamily: 'Nunito', display: 'block', marginBottom: 8 }}>
                              {step.age}
                            </MyText>
                          </Col>
                          <Col>
                            <MyText color="#364153" size={16} weight={600} style={{ fontFamily: 'Nunito', display: 'block' }}>
                              {step.text}
                            </MyText>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>

          <Col span={24}>
            <MyText color="#0A033C" size={32} weight={800} style={{ fontFamily: 'Nunito', display: 'block' }}>
              {childAgeLabel
                ? `Возраст Вашего ребёнка ${childAgeLabel}`
                : AGE_LABELS[selectedAgeKey]
              }
            </MyText>
          </Col>

          <Col span={24}>
            <MyText color="#0A033C" size={18} weight={600} style={{ fontFamily: 'Nunito', display: 'block' }}>
              Что должен уметь ребёнок:
            </MyText>
          </Col>

          <Col span={24}>
            <Row gutter={[0, 0]}>
              {questions.map((question, index) => (
                <Col span={24} key={`${selectedAgeKey}-${index}`}>
                  <Row align="middle">
                    <Col span={20}>
                      <MyText color="#2E2E31" size={18} weight={400} style={{ fontFamily: 'Nunito', display: 'block' }}>
                        {question}
                      </MyText>
                    </Col>
                    <Col span={4}>
                      <Row justify="end">
                        <YesNoButton
                          value={getAnswer(index)}
                          onChange={(val) => handleAnswer(index, val)}
                        />
                      </Row>
                    </Col>
                  </Row>
                  <Divider style={{ margin: '17px 0', borderColor: '#E5E9F2' }} />
                </Col>
              ))}
            </Row>

            <Row justify="start" style={{ marginTop: 8 }}>
              <MyButton
                color="orange"
                type="primary"
                disabled={!allAnswered}
                onClick={handleShowResult}
              >
                Показать результат
              </MyButton>
            </Row>
          </Col>
        </Row>
      </div>

      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        centered
        width={550}
      >
        <Col span={24} style={{ padding: '16px 0', textAlign: 'center', alignItems: 'center', }}>
          <Row justify="center" style={{ marginBottom: 16 }}>
           <div
              style={{
                width: 112,
                height: 112,
                borderRadius: '50%',
                background: `${result.color}1A`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: result.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="32" height="25" viewBox="0 0 14 11" fill="none">
                  <path
                    d="M1 5L5 9L13 1"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </Row>

          <MyText color="#0A033C" size={32} weight={800} style={{ fontFamily: 'Nunito', display: 'block', marginBottom: 24, textAlign: 'center', }}>
            Результат тестирования
          </MyText>

          <MyText color="#364153" size={18} weight={500} style={{ fontFamily: 'Nunito', display: 'block', marginBottom: 40, textAlign: 'center', }}>
            {yesCount}/{questions.length} «Да» - {result.title}
          </MyText>

          <Row gutter={[0, 8]}>
            <Col span={24} style={{ marginTop: 8 }}>
              <Row gutter={8} justify="center">
                <Col>
                  <MyButton color="orange" type="primary" onClick={() => setModalVisible(false)}>
                    Закрыть
                  </MyButton>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Modal>

    {showFooter && (
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#FF9500',
        borderTop: '1px solid #E5E9F2',
        padding: '16px 24px',
        display: 'flex',
        zIndex: 100, 
        justifyContent: 'center',
        boxShadow: '0 -4px 16px rgba(0,0,0,0.06)',
        marginTop: '16px',
      }}>
        <MyButton
          type="default"
          onClick={() => embeddedNavigate('/speech-marathon')}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            color: hovered ? '#fcb047' : '#FF9500',
            border: `1px solid ${hovered ? '#fcb047' : '#FF9500'}`,
            background:'#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            transition: 'all 0.2s ease',
          }}
        >
          Нажмите чтобы начать марафон
        </MyButton>
      </div>
      )}

      <ChildSelectModal
          visible={childSelectVisible}
          selectedId={localSelectedChildId}
          onSelect={(id) => setLocalSelectedChildId(id)}
          onConfirm={handleChildConfirm}
          minAgeMonths={12}
          maxAgeMonths={48}
          />
    </section>
  );
};